import React from "react";
import _ from "lodash";
import {Box} from "@mui/system";
import Button from "@mui/material/Button";
import {PageHeader} from "components/molecules/PageHeader/PageHeader";
import BoardTable from "components/atoms/BoardTable/BoardTable";
import {useSelector} from 'react-redux';
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import {Grid, Skeleton, TableSortLabel} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import {Link as RouterLink} from "react-router-dom";
import SimulationSelection from "components/molecules/SimulationSelection/SimulationSelection";
import {selectAllSimulations} from "redux/simulation/simulationSlice";
import {routes} from "routes";
import MenuItem from "@mui/material/MenuItem";
import {ResultsDisclaimer} from "components/molecules/ResultsDisclaimer/ResultsDisclaimer";
import SettingsDropdown from "components/molecules/SettingsDropdown/SettingsDropdown";
import Tooltip from "@mui/material/Tooltip";
import {
  formatPercentage,
  formatCurrency,
  getValueColor
} from "utils/metricFormatters";

// Helper function to format ISO date to YYYY-MM-DD
const formatDate = (isoDate) => {
  if (!isoDate) return 'N/A';
  return new Date(isoDate).toISOString().split('T')[0];
};

// Helper function to safely convert values to numbers
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  // Handle both string and number inputs
  const numberValue = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(numberValue) ? 0 : numberValue;
};

export default function Simulation() {
  const simulations = useSelector(selectAllSimulations);
  const strategies = useSelector(state => state.strategy.entities);
  const loading = useSelector(state => state.simulation.loading);

  return (
    <>
      <PageHeader text1={"Simulation"} text2={"Strategies"} title="Dashboard - Simulation Account" />

      {_.isEmpty(simulations) ?  <SimulationSelection /> :
      <>
      <BoardTable>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell colSpan={2} align="center" className="light" style={{ borderWidth: 0, borderTopWidth: '2px'}}>
              <Typography variant="mediumText" color="secondary.dark" >ALLOCATION</Typography>
            </TableCell>
            <TableCell align="center" className="light" style={{ borderWidth: 0, borderTopWidth: '2px'}} ><Typography variant="mediumText" color="secondary.dark" >TODAY</Typography></TableCell>
            <TableCell align="center" className="light" style={{ borderWidth: 0, borderTopWidth: '2px'}}><Typography variant="mediumText" color="secondary.dark" >ALL TIME</Typography></TableCell>
          </TableRow>
          <TableRow>
            <TableCell rowSpan={2}>Strategy</TableCell>
            <TableCell rowSpan={2} align="center"><TableSortLabel active={true} direction="desc">Started</TableSortLabel></TableCell>
              <TableCell align="center"><TableSortLabel>Starting</TableSortLabel></TableCell>
              <TableCell align="center"><TableSortLabel>Latest</TableSortLabel></TableCell>
            <TableCell align="center"><TableSortLabel>Change %</TableSortLabel></TableCell>
            <TableCell align="center"><TableSortLabel>Change %</TableSortLabel></TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {simulations.map((simulation, index) => {
            const strategy = strategies[simulation.strategy];
            
            // Get metrics from simulation performance_metrics
            const metrics = simulation.performance_metrics || {};
            const todayChange = metrics.value_change_today || { value: 0, percentage: 0 };
            const totalChange = metrics.value_change_total || { value: 0, percentage: 0 };
            
            // Format date and values
            const startDate = formatDate(simulation.created);
            const startingValue = metrics.starting_value ? 
              formatCurrency(safeParseFloat(metrics.starting_value)) : 
              (strategy ? formatCurrency(safeParseFloat(strategy.min_capital)) : 'N/A');
            const latestValue = metrics.value ? formatCurrency(safeParseFloat(metrics.value)) : 'N/A';
            
            // Format change values with percentage
            const todayChangePct = formatPercentage(safeParseFloat(todayChange.percentage));
            const totalChangePct = formatPercentage(safeParseFloat(totalChange.percentage));
            
            return (
              strategy && (
                <TableRow key={simulation.id}>
                  <TableCell className="light2" >
                    <Grid container>
                      <Grid item md={12} lg={6}>
                        <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                          <Typography variant="h4" sx={{mb:1}} color="primary"> {strategy.name.toUpperCase()} </Typography>
                        </RouterLink>
                      </Grid>
                      <Grid item md={12} lg={6}>
                        <Box display="flex" justifyContent={{xs: "flex-start", lg: "flex-end"}} mt={{xs: 1, lg: 0}}>
                          <RouterLink>
                        <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                          <Button color="primary" variant="outlined" size="small" > Details </Button>
                        </RouterLink>
                          </RouterLink>
                        </Box>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color="secondary.dark">{startDate}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color="secondary.dark">{startingValue}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color="secondary.dark">{latestValue}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color={getValueColor(safeParseFloat(todayChange.percentage))}>
                        {todayChangePct}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color={getValueColor(safeParseFloat(totalChange.percentage))}>
                        {totalChangePct}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{width: 80}}>
                   <SettingsDropdown>
                     <MenuItem component={RouterLink} to={`${routes.autoTrade}`} >Manage Simulation &nbsp;(AutoTrade)</MenuItem>
                     <MenuItem component={RouterLink} to={routes.strategyDetails.replace(":strategyId", strategy.id)}>Subscribe to Strategy</MenuItem>
                     <Tooltip title="You have to subscribe to enable AutoTrading" >
                       <span sx={{mb: 1}}> <MenuItem disabled>Change to Live &nbsp; (AutoTrade)</MenuItem> </span>
                     </Tooltip>
                   </SettingsDropdown>
                  </TableCell>
                </TableRow>
              )
            )}
          )}
        </TableBody>
      </BoardTable>


      <Box sx={{mt: 5}}>
        <ResultsDisclaimer/>
      </Box>
      </>
      }
    </>
  )
}